import type { DesignAssetView, PlacementPosition, SizeMm, Surface } from '@repo/api-contracts';
import {
  type PhotoDimensions,
  projectGuideToFlat,
  type RenderMaterials,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import { useEffect, useMemo, useState } from 'react';
import type { FontFaces } from '@/entities/design';
import { createRasterCache } from '@/features/design-editor';
import { loadImageRaster } from '@/shared/lib';

interface CustomizeMaterialsInput {
  readonly assetUrls: ReadonlyMap<string, DesignAssetView>;
  readonly fonts: FontFaces;
  readonly fontIds: readonly string[];
  readonly inkColors: readonly { readonly id: string; readonly colorCode: string }[];
  readonly guideUrl: string | null;
  readonly surface: Surface | null;
  readonly photo: PhotoDimensions | null;
  readonly component: SizeMm | null;
  readonly position: PlacementPosition | null;
  readonly pxPerMm: number;
}

/** 평면 편집이 쓰는 재료 — 고객 이미지 래스터(assetId 캐시), 로드된 폰트, 잉크, 선택 중 안내선 역투영(면·축척별 캐시). */
export const useCustomizeMaterials = (
  input: CustomizeMaterialsInput,
): { materials: RenderMaterials; guide: Raster | null } => {
  const cache = useMemo(() => createRasterCache(loadImageRaster), []);
  const [images, setImages] = useState<ReadonlyMap<string, Raster>>(new Map());
  const [guide, setGuide] = useState<Raster | null>(null);
  const { assetUrls, guideUrl, surface, photo, component, position, pxPerMm } = input;

  useEffect(() => {
    let cancelled = false;
    for (const [assetId, asset] of assetUrls) {
      if (images.has(assetId)) continue;
      void cache
        .get(assetId, asset.url)
        .then((raster) => {
          if (!cancelled) setImages((prev) => new Map(prev).set(assetId, raster));
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [assetUrls, cache, images]);

  useEffect(() => {
    if (
      guideUrl === null ||
      surface === null ||
      photo === null ||
      component === null ||
      position === null
    ) {
      setGuide(null);
      return;
    }
    let cancelled = false;
    void cache
      .get(`guide:${guideUrl}`, guideUrl)
      .then((raster) => {
        if (!cancelled)
          setGuide(
            projectGuideToFlat({ guide: raster, surface, photo, component, position, pxPerMm }),
          );
      })
      .catch(() => {
        if (!cancelled) setGuide(null);
      });
    return () => {
      cancelled = true;
    };
  }, [guideUrl, surface, photo, component, position, pxPerMm, cache]);

  const materials = useMemo<RenderMaterials>(
    () => ({
      images,
      fonts: new Map(
        input.fontIds.flatMap((id) => {
          const family = input.fonts.familyOf(id);
          return family === undefined ? [] : [[id, { familyName: family }] as const];
        }),
      ),
      inks: new Map(input.inkColors.map((ink) => [ink.id, ink.colorCode])),
    }),
    [images, input.fontIds, input.fonts, input.inkColors],
  );
  return { materials, guide };
};
